import { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { api } from '@/core/api/queries';
import { useReportPage } from '@/shared/hooks';
import { SimpleReportLayout } from '@/shared/components/layouts';
import { DataTable, SpringPagination, ReportControls, EditionSelector } from '@/shared/components/tables';
import { columnBuilder } from '@/shared/utils';
import { usersApi } from '../../users/api/users.api';
import { schoolsApi } from '../../schools/api/schools.api';
import { UserDataPreviewDialog } from '../../users/components/UserDataPreviewDialog';
import { SchoolDataPreviewDialog } from '../../schools/components/SchoolDataPreviewDialog';
import type { UserProfileDto } from '@/api/generated/types.gen';
import type { SchoolReportDto } from '../../schools/api/schools.api';
import type { StudentLeaderboardRow } from '../api/students.api';

/**
 * Students Leaderboard Report Page ("Top Elevi" / clasament elevi pentru premiere)
 *
 * The backend returns the FULL leaderboard already ordered by `total` descending,
 * each row carrying a `loc` (dense rank on `total`) that is the prize rank and
 * stays fixed. The backend ignores per-column `sort`, so:
 *  - we fetch the whole set once (a single large Page), and
 *  - we sort + paginate CLIENT-SIDE,
 * while rendering through the exact same visual components as the other reports
 * (ReportControls + DataTable + SpringPagination inside SimpleReportLayout).
 *
 * Drill-down to winners: each row carries `userId` and (optionally) `schoolId`, so
 * the "Elev" and "Școala" cells are clickable and open the existing detail dialogs
 * (UserDataPreviewDialog / SchoolDataPreviewDialog) — the same pattern used by
 * UsersReportPage / EditionStatusReportPage. This lets an ADMIN actually reach a
 * winner (contact details, parental approval, school's professor) from the table.
 *
 * Mirrors the UX of /rapoarte/grafic-activitati (SchoolsGraphicReportPage), including
 * the edition selector. Prize winners (loc <= 5, ties included) get a subtle highlight.
 *
 * Role-based access (ADMIN + DBEST) is enforced via ALLOWED_ROLES in routes-provider.
 */
export function StudentsLeaderboardReportPage() {
    // ==========================================
    // 1. DATA FETCHING (fetch the entire set once)
    // ==========================================

    const { report, handleExport, editions, currentEdition, changeEdition } = useReportPage<StudentLeaderboardRow>({
        queryHook: api.students.leaderboard,
        initialSort: [],          // backend ignores per-column sort
        initialPageSize: 10000,   // fetch the whole leaderboard in one page (~7700 rows)
        exportEndpoint: '/api/students/leaderboard',
        exportFilename: 'Top elevi',
        withEditions: true,
    });

    // ==========================================
    // 2. CLIENT-SIDE SORT & PAGINATION STATE
    // ==========================================

    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [clientPage, setClientPage] = useState(0);
    const [clientSize, setClientSize] = useState(20);

    // ==========================================
    // 3. DETAIL DIALOG STATE (drill-down to winner)
    // ==========================================

    const [studentDialogOpen, setStudentDialogOpen] = useState(false);
    const [studentData, setStudentData] = useState<UserProfileDto | null>(null);
    const [loadingStudent, setLoadingStudent] = useState(false);

    const [schoolDialogOpen, setSchoolDialogOpen] = useState(false);
    const [schoolData, setSchoolData] = useState<SchoolReportDto | null>(null);
    const [loadingSchool, setLoadingSchool] = useState(false);

    const openStudentDialog = useCallback(async (userId: string, editionId?: string) => {
        setStudentData(null);
        setLoadingStudent(true);
        setStudentDialogOpen(true);
        try {
            const profile = await usersApi.getProfile({ userId, editionId: editionId || currentEdition?.id });
            setStudentData(profile);
        } catch (error) {
            console.error('Error loading student profile:', error);
            toast.error('Eroare la încărcarea detaliilor elevului.');
            setStudentDialogOpen(false);
        } finally {
            setLoadingStudent(false);
        }
    }, [currentEdition]);

    const openSchoolDialog = useCallback(async (schoolId: string) => {
        setSchoolData(null);
        setLoadingSchool(true);
        setSchoolDialogOpen(true);
        try {
            const profile = await schoolsApi.getSchoolProfile(schoolId);
            setSchoolData(profile);
        } catch (error) {
            console.error('Error loading school profile:', error);
            toast.error('Eroare la încărcarea detaliilor școlii.');
            setSchoolDialogOpen(false);
        } finally {
            setLoadingSchool(false);
        }
    }, []);

    // ==========================================
    // 4. CLIENT-SIDE SORTING
    // ==========================================

    const sorted = useMemo(() => {
        const rows = report.data;
        // No active column sort → keep backend order (already total desc).
        if (!sortField) return rows;

        const dir = sortDir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = a[sortField as keyof StudentLeaderboardRow];
            const bv = b[sortField as keyof StudentLeaderboardRow];

            if (typeof av === 'number' && typeof bv === 'number') {
                return (av - bv) * dir;
            }
            return String(av ?? '').localeCompare(String(bv ?? ''), 'ro') * dir;
        });
    }, [report.data, sortField, sortDir]);

    // ==========================================
    // 5. CLIENT-SIDE PAGINATION
    // ==========================================

    const totalElements = sorted.length;
    const totalPages = Math.max(1, Math.ceil(totalElements / clientSize));

    const paged = useMemo(
        () => sorted.slice(clientPage * clientSize, (clientPage + 1) * clientSize),
        [sorted, clientPage, clientSize]
    );

    // ==========================================
    // 6. COLUMNS (all sortable, no filter inputs)
    // ==========================================
    // "Elev" and "Școala" are clickable → open the detail dialogs.

    const columns = useMemo(
        () =>
            columnBuilder<StudentLeaderboardRow>()
                .number('loc', 'Loc')
                .text('nume', 'Elev', undefined, {
                    render: (_, row) => (
                        <button
                            type="button"
                            disabled={!row.userId || loadingStudent}
                            onClick={() => row.userId && openStudentDialog(row.userId, row.editionId)}
                            className="text-left font-medium text-magenta hover:underline focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
                            title="Vezi detalii elev"
                        >
                            {row.nume || '-'}
                        </button>
                    ),
                })
                .text('clasa', 'Clasa')
                .text('scoala', 'Școala', undefined, {
                    render: (_, row) =>
                        row.schoolId ? (
                            <button
                                type="button"
                                disabled={loadingSchool}
                                onClick={() => row.schoolId && openSchoolDialog(row.schoolId)}
                                className="text-left font-medium text-magenta hover:underline focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
                                title="Vezi detalii școală"
                            >
                                {row.scoala || '-'}
                            </button>
                        ) : (
                            <span>{row.scoala || '-'}</span>
                        ),
                })
                .text('judet', 'Județ')
                .number('puncteHomework', 'Puncte teme')
                .number('puncteMonthly', 'Puncte lunare')
                .number('total', 'Total')
                .build(),
        [loadingStudent, loadingSchool, openStudentDialog, openSchoolDialog]
    );

    // ==========================================
    // 7. RENDER
    // ==========================================

    return (
        <>
            <SimpleReportLayout
                title="Top Elevi"
                controls={
                    <ReportControls
                        pagination={{
                            pageNumber: clientPage,
                            totalPages,
                            totalElements,
                            pageSize: clientSize,
                        }}
                        isLoading={report.loading}
                        onRefresh={report.refetch}
                        onResetFilters={() => {
                            setSortField(null);
                            setClientPage(0);
                        }}
                        onExport={handleExport}
                        extraControls={
                            <EditionSelector
                                editions={editions || []}
                                currentEditionId={currentEdition?.id}
                                onChange={changeEdition || (() => {})}
                                disabled={report.loading}
                            />
                        }
                    />
                }
                pagination={
                    <SpringPagination
                        currentPage={clientPage}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        pageSize={clientSize}
                        onPageChange={setClientPage}
                        onPageSizeChange={(s) => {
                            setClientSize(s);
                            setClientPage(0);
                        }}
                    />
                }
                loading={report.loading}
            >
                <DataTable
                    columns={columns}
                    data={paged}
                    onSort={(field, dir) => {
                        setSortField(field);
                        setSortDir(dir);
                        setClientPage(0);
                    }}
                    currentSort={sortField ? `${sortField},${sortDir}` : null}
                    // Podiumul trece prin `--ecotic-row-bg`, nu printr-un `bg-*`:
                    // regula nelayerata de fundal de rand din main.css ar bate orice
                    // clasa de fundal pusa aici.
                    rowClassName={(row) => (row.loc <= 5 ? 'ecotic-table-row-rank' : undefined)}
                />
            </SimpleReportLayout>

            {/* Drill-down dialogs (reused from users/schools domains) */}
            <UserDataPreviewDialog
                isOpen={studentDialogOpen}
                onClose={() => setStudentDialogOpen(false)}
                data={studentData}
                isLoading={loadingStudent}
            />
            <SchoolDataPreviewDialog
                isOpen={schoolDialogOpen}
                onClose={() => setSchoolDialogOpen(false)}
                data={schoolData}
                isLoading={loadingSchool}
            />
        </>
    );
}
